// src/components/CloudGPU.jsx
import React, { useState } from "react";
import {
  Cpu,
  Cloud,
  Globe2,
  Server,
  Boxes,
  Zap,
  BookOpen,
  FileCode2,
  HelpCircle,
} from "lucide-react";
import FadeInOnScroll from "./FadeInOnScroll"; // 名字随便，但首字母要大写
import FadeInCard from "./FadeInCard";
import SlideInOnScroll from "./SlideInOnScroll";
import { Link } from "react-router-dom";
export default function CloudGPU() {
  // FAQ 数据
  // const faqs = [
  //   {
  //     question: "How does Cloud GPU work?",
  //     answer:
  //       "A Cloud GPU works by providing access to GPU instances located in secure data centers. This enables users to run intensive AI training, inference, and rendering workloads—without needing a physical GPU.",
  //   },
  //   {
  //     question: "What are the benefits of Cloud GPU?",
  //     answer:
  //       "Cloud GPU lets you instantly scale up or down, pay only for what you use, and access the latest AMD and NVIDIA architectures without large upfront hardware investments.",
  //   },
  //   {
  //     question: "What use cases need Cloud GPU?",
  //     answer:
  //       "Typical use cases include large-scale model training, fine-tuning, real-time inference, 3D rendering, video processing, and high-performance computing workloads.",
  //   },
  //   {
  //     question: "How to choose the right GPU?",
  //     answer:
  //       "Choose based on VRAM requirements, compute performance, precision support, and budget. For large models, H100 and MI300X are ideal; for inference, L40S, A100 or A40 are popular choices.",
  //   },
  //   {
  //     question: "Cloud GPU vs traditional GPU?",
  //     answer:
  //       "Compared with owning hardware, Cloud GPU reduces upfront cost, simplifies operations, and allows you to dynamically allocate resources for different projects and teams.",
  //   },
  //   {
  //     question: "Can I mix Cloud GPU with CPUs only?",
  //     answer:
  //       "Yes. Many customers combine GPU instances for training and CPU instances for preprocessing, orchestration, and lightweight services in the same virtual network.",
  //   },
  //   {
  //     question: "How do I migrate existing models?",
  //     answer:
  //       "Package your models and dependencies into containers, push them to our registry, and deploy onto Cloud GPU nodes or serverless inference endpoints.",
  //   },
  // ];

  const gpus = [
    {
      title: "AMD Instinct™ MI325X",
      desc: "High-memory AMD accelerator ideal for large-scale model training and mixed inference workloads.",
    },
    {
      title: "NVIDIA H100 GPU",
      desc: "Designed for cutting-edge AI training and inference, with exceptional performance for LLMs and vision models.",
    },
    {
      title: "NVIDIA GH200 Grace Hopper™",
      desc: "CPU–GPU superchip for tightly coupled AI, HPC and data-intensive workloads with massive memory bandwidth.",
    },
    {
      title: "NVIDIA A100 Tensor Core GPU",
      desc: "A versatile GPU for training, inference, and data analytics, powering a wide range of AI workloads.",
    },
    {
      title: "NVIDIA L40S GPU",
      desc: "Optimized for graphics, rendering, and generative AI inference with strong efficiency for production.",
    },
    {
      title: "NVIDIA A40 GPU",
      desc: "Perfect for visualization, graphics workloads, and medium-scale inference in production environments.",
    },
    {
      title: "NVIDIA A16 GPU",
      desc: "High-density GPU for virtual desktops and lightweight inference, ideal for multi-tenant environments.",
    },
    {
      title: "NVIDIA A16 GPU",
      desc: "High-density GPU for virtual desktops and lightweight inference, ideal for multi-tenant environments.",
    },
    {
      title: "NVIDIA A16 GPU",
      desc: "High-density GPU for virtual desktops and lightweight inference, ideal for multi-tenant environments.",
    },
  ];

  // const [activeFaqIndex, setActiveFaqIndex] = useState(0);
  // const activeFaq = faqs[activeFaqIndex];

  return (
    <main className="relative overflow-hidden bg-gradient-to-b from-purple-50 via-white to-purple-100 text-[#0f0533]">
      {/* 背景柔光 */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-24 -top-10 h-[26rem] w-[26rem] rounded-full bg-purple-300/25 blur-3xl" />
        <div className="absolute right-[-10rem] top-40 h-[30rem] w-[30rem] rounded-full bg-purple-200/30 blur-3xl" />
        <div className="absolute bottom-[-8rem] left-1/3 h-[22rem] w-[22rem] rounded-full bg-fuchsia-200/30 blur-3xl" />
      </div>

      {/* HERO */}
      <section className="relative max-w-6xl mx-auto px-6 pt-32 pb-32 grid gap-12 md:grid-cols-[1.4fr_1fr] items-center">
        <div>
          <FadeInOnScroll>
            <p className="text-sm tracking-[0.3em] font-semibold text-[#7b5cff] mb-3 uppercase">
              Cloud GPU
            </p>
            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-4 bg-gradient-to-r from-[#5b21b6] via-[#7c3aed] to-[#a855f7] bg-clip-text text-transparent">
              High-Performance
              <br />
              Cloud GPU Solutions
            </h1>
            <p className="text-base md:text-lg text-[#3b2c70] max-w-xl">
              Access the latest AMD and NVIDIA GPUs on demand for AI/ML,
              high-performance computing, 3D/CGI, and more. Deploy in minutes,
              scale globally, and only pay for what you use.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                className="rounded-full bg-gradient-to-r from-[#6a2dd9] to-[#9a8dfc] px-6 py-3 text-sm font-semibold text-white shadow-[0_10px_35px_rgba(106,45,217,0.35)] hover:translate-y-[-2px] transition-transform inline-flex items-center justify-center"
              >
                Reserve Cloud GPU capacity
              </Link>
              <p className="text-xs md:text-sm text-[#5b4a99]">
                Flexible on-demand VMs and dedicated clusters.
              </p>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* GPU INFRASTRUCTURE */}
      <section className="relative py-20">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <SlideInOnScroll from="left">
            <p className="text-xs tracking-[0.3em] text-[#9f7aea] uppercase mb-3 font-semibold">
              Cloud GPU infrastructure
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2a0f4c] mb-3">
              State-of-the-art Cloud GPU infrastructure
            </h2>
            <p className="text-sm md:text-base text-[#6b4cae] max-w-2xl mx-auto mb-10">
              Powered by advanced AMD accelerators and NVIDIA-accelerated
              computing to handle your most demanding AI and high-performance
              workloads.
            </p>
          </SlideInOnScroll>

          {/* 卡片：hover 时显示 description */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 text-lg">
            {gpus.map((gpu, index) => (
              <FadeInCard key={gpu.title}>
                <div className="group relative cursor-pointer rounded-2xl bg-white/80 backdrop-blur-xl border border-purple-100 px-4 py-10 shadow-[0_10px_30px_rgba(148,101,255,0.18)] hover:shadow-[0_16px_40px_rgba(148,101,255,0.3)] transition-all duration-300">
                  {/* 默认状态 */}
                  <div className="flex flex-col items-center justify-between gap-4 h-full group-hover:opacity-0 group-hover:translate-y-2 transition-all duration-300">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#a855f7]/20 via-[#7c3aed]/10 to-[#c4b5fd]/40 flex items-center justify-center">
                      <Cpu className="w-5 h-5 text-[#7c3aed]" />
                    </div>
                    <p className="text-[16px] text-[#3b2c70] text-center">
                      {gpu.title}
                    </p>
                    <div className="mt-2 flex items-center justify-center">
                      <div className="w-7 h-7 rounded-full bg-[#7c3aed] flex items-center justify-center shadow-[0_6px_16px_rgba(124,58,237,0.45)]">
                        <span className="text-white text-sm leading-none">
                          +
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* hover 状态：description */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-[#f6edff] via-white to-[#ece3ff] border border-purple-200 px-5 py-5 text-left flex flex-col justify-between opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                    <div>
                      <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#a855f7] mb-2">
                        {gpu.title}
                      </p>
                      <p className="text-xs text-[#4c3670] leading-relaxed">
                        {gpu.desc}
                      </p>
                    </div>
                    <button className="self-end mt-4 text-xs font-semibold text-[#7c3aed] inline-flex items-center gap-1">
                      Learn more
                      <span>↗</span>
                    </button>
                  </div>
                </div>
              </FadeInCard>
            ))}
          </div>

          {/* Logo 区块（浅色） */}
          {/* Logo 区块（浅色） */}
          <div className="mt-12 flex flex-wrap justify-center items-center gap-12 text-sm text-[#6b4cae]">
            {/* AMD logo */}
            {/* <div className="flex items-center gap-4">
              <img
                src="/partners/amd2.png"
                alt="AMD Cloud Partner"
                className="h-10 md:h-12 w-auto"
              />
              <span>AI NOVA Cloud Alliance Partner</span>
            </div> */}

            {/* NVIDIA logo */}
            {/* <div className="flex items-center gap-4">
              <img
                src="/partners/nvidea.png"
                alt="NVIDIA Preferred Partner"
                className="h-10 md:h-12 w-auto"
              />
              <span>Preferred NVIDIA Partner</span>
            </div> */}
          </div>
        </div>
      </section>

      {/* DEVELOP LOCALLY, DEPLOY GLOBALLY */}
      <section className="relative py-20">
        <div className="max-w-6xl mx-auto px-6 grid gap-14">
          {/* 文案 + 小卡片 */}
          <div className="grid md:grid-cols-[1.3fr_1fr] gap-10 items-center">
            <div>
              <SlideInOnScroll from="left">
                <p className="text-xs tracking-[0.3em] text-[#a855f7] uppercase mb-3 font-semibold">
                  Global reach
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-[#2a0f4c] mb-4">
                  Develop locally, deploy globally®
                </h2>
                <p className="text-base text-[#3b2c70] max-w-xl">
                  Launch GPU-accelerated clusters in any of our global regions.
                  Low-latency connectivity, predictable performance, and the
                  flexibility to spin up resources in seconds.
                </p>
              </SlideInOnScroll>
            </div>
            <SlideInOnScroll from="right">
              <div className="rounded-3xl bg-white/85 backdrop-blur-xl border border-purple-200 p-6 shadow-[0_12px_35px_rgba(136,84,255,0.25)]">
                <p className="text-sm font-semibold text-[#2a0f4c] mb-3">
                  Large-scale clusters & on-demand VMs
                </p>
                <p className="text-xs text-[#4b3a82] mb-4">
                  Mix dedicated GPU nodes with burstable on-demand instances to
                  handle training, fine-tuning, and inference — all on the same
                  platform.
                </p>
                {/* <button className="text-xs font-semibold text-[#7c3aed] underline underline-offset-4">
                  Download Cloud GPU overview
                </button> */}
              </div>
            </SlideInOnScroll>
          </div>

          {/* 地图卡片（浅色） */}
          {/* <div className="relative rounded-3xl bg-gradient-to-br from-purple-50 via-white to-purple-100 border border-purple-100 px-8 py-10 overflow-hidden shadow-[0_20px_60px_rgba(136,84,255,0.25)]">
            <div className="absolute inset-0 opacity-70 pointer-events-none">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(196,181,253,0.4),_transparent)]" />
            </div>

            <div className="relative flex flex-col md:flex-row items-center justify-between gap-10">
              <div className="md:w-[45%]">
                <h3 className="text-2xl font-bold text-[#2a0f4c] mb-2">
                  5 cloud data center regions
                </h3>
                <p className="text-md text-[#4c3670] max-w-md">
                  Strategically located regions across the globe provide
                  low-latency access to GPU capacity wherever your teams and
                  customers are.
                </p>
                <button className="mt-6 rounded-full border border-[#a855f7]/50 px-5 py-2 text-sm font-semibold text-[#7c3aed] hover:bg-purple-50">
                  Explore regions
                </button>
              </div>

              <div className="relative w-full md:w-[55%] aspect-[16/9] rounded-3xl border border-purple-200 bg-white/80 overflow-hidden shadow-[0_18px_45px_rgba(148,101,255,0.25)] flex items-center justify-center">
                <img
                  src="/asia-map-purple.png"
                  alt="AI NOVA Asia data center regions map"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            </div>
          </div> */}
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 text-center md:text-left">
              <div className="space-y-6 text-justify">
                <SlideInOnScroll from="left">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4 bg-gradient-to-r from-[#ede3ff]/60 to-[#f9f6ff]/80 rounded-xl p-5 shadow-[0_8px_25px_rgba(138,79,255,0.1)]">
                    <div className="flex-shrink-0 w-10 h-10 mx-auto sm:mx-0 rounded-lg bg-[#6a2dd9] flex items-center justify-center text-white font-bold">
                      ✓
                    </div>
                    <p className="text-[#2a0f4c]/90 leading-relaxed text-[1rem] sm:text-[1.05rem]">
                      <strong>AI NOVA</strong> state-of-the-art data centers are
                      strategically located in powerhouse cities across various
                      countries to deliver unmatched compute power, speed,
                      resilience, and access to targeted markets.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-start gap-4 bg-gradient-to-r from-[#ede3ff]/60 to-[#f9f6ff]/80 rounded-xl p-5 shadow-[0_8px_25px_rgba(138,79,255,0.1)]">
                    <div className="flex-shrink-0 w-10 h-10 mx-auto sm:mx-0 rounded-lg bg-[#6a2dd9] flex items-center justify-center text-white font-bold">
                      ✓
                    </div>
                    <p className="text-[#2a0f4c]/90 leading-relaxed text-[1rem] sm:text-[1.05rem]">
                      <strong>AI NOVA</strong> data centers comprise
                      custom-configured high-performance compute nodes,
                      resilient storage, ultra-low latency networking, and
                      GPU-accelerated servers.
                    </p>
                  </div>
                </SlideInOnScroll>
              </div>
            </div>
            {/* ===== 左侧说明区 ===== */}
            <SlideInOnScroll from="right">
              <div className="relative flex justify-center items-center mt-10 md:mt-0">
                <div className="relative flex justify-center items-center mt-10 md:mt-0">
                  <img
                    src="/asia-map-purple.png"
                    alt="Asia Data Centers"
                    className="w-full h-auto max-w-none object-contain opacity-95 drop-shadow-[0_10px_20px_rgba(106,45,217,0.15)]"
                  />
                </div>
              </div>
            </SlideInOnScroll>

            {/* ===== 右侧地图 ===== */}
          </div>
        </div>
      </section>

      {/* KUBERNETES & SERVERLESS */}
      <section className="relative py-20 bg-gradient-to-b from-purple-50/80 via-white to-purple-100">
        <FadeInOnScroll>
          <div className="max-w-6xl mx-auto px-6 grid gap-10 md:grid-cols-2">
            {/* Kubernetes */}
            <div className="rounded-3xl bg-white/85 backdrop-blur-xl border border-purple-200 p-8 shadow-[0_10px_30px_rgba(136,84,255,0.2)]">
              <div className="flex items-center gap-3 mb-4">
                <Boxes className="w-7 h-7 text-[#7c3aed]" />
                <h3 className="text-xl font-bold text-[#2a0f4c]">
                  Kubernetes Engine for Cloud GPU
                </h3>
              </div>
              <p className="text-sm text-[#3b2c70] mb-4">
                Orchestrate GPU-accelerated Kubernetes clusters with ease.
                Auto-scale workers, attach block storage, and integrate with
                your CI/CD pipelines to support the full lifecycle of AI-powered
                applications.
              </p>
              <Link
                to="/contact"
                className="text-sm font-semibold text-[#7c3aed] underline underline-offset-4"
              >
                Contact Sales
              </Link>
            </div>

            {/* Serverless Inference */}
            <div className="rounded-3xl bg-gradient-to-br from-[#f5e9ff] via-white to-[#ecebff] border border-purple-200 p-8 shadow-[0_10px_35px_rgba(136,84,255,0.26)]">
              <div className="flex items-center gap-3 mb-4">
                <Zap className="w-7 h-7 text-[#8b5cf6]" />
                <h3 className="text-xl font-bold text-[#2a0f4c]">
                  Serverless GPU Inference
                </h3>
              </div>
              <p className="text-sm text-[#3b2c70] mb-4">
                Deploy and scale inference endpoints without managing servers.
                Pay only for the compute you use while benefiting from
                GPU-backed performance for generative, vision, and traditional
                ML workloads.
              </p>
              <Link
                to="/contact"
                className="text-sm font-semibold text-[#7c3aed] underline underline-offset-4"
              >
                Contact Sales
              </Link>
            </div>
          </div>
        </FadeInOnScroll>
      </section>

      {/* CLOUD-NATIVE APPLICATIONS & BUILT FOR BUSINESS （改成紫色系） */}
      <section className="relative py-20 bg-gradient-to-b from-purple-50 via-white to-purple-100">
        <div className="max-w-6xl mx-auto px-6 space-y-16">
          {/* Cloud-native 3 条 */}
          <div>
            <FadeInOnScroll>
              <p className="text-xs font-semibold tracking-[0.35em] text-[#a855f7] uppercase mb-4">
                Develop, deploy, and optimize
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2a0f4c] mb-10 leading-tight">
                Cloud-native applications that run
                <br className="hidden md:block" />
                every aspect of your business
              </h2>
            </FadeInOnScroll>

            <div className="border-y border-purple-100/70 divide-y divide-purple-100/70">
              {/* Marketplace */}
              <div className="py-8 grid gap-6 md:grid-cols-[0.2fr_0.95fr_1.1fr] items-start">
                <div className="flex justify-start">
                  <div className="w-16 h-16 rounded-full border-2 border-[#c4b5fd] flex items-center justify-center">
                    <Cloud className="w-7 h-7 text-[#8b5cf6]" />
                  </div>
                </div>
                <div>
                  <SlideInOnScroll from="left">
                    <h3 className="text-lg font-semibold text-[#3b2c70] mb-2">
                      Instantly discover applications and services
                      <br className="hidden md:block" /> in AI NOVA Marketplace
                    </h3>
                  </SlideInOnScroll>
                </div>
                <SlideInOnScroll from="right">
                  <div className="space-y-2 text-sm">
                    <p className="text-[#4b3a82] leading-relaxed">
                      Harness plug-and-play SaaS and AI tools to make developing
                      and deploying your cloud workloads easier.
                    </p>
                    <button className="text-sm font-semibold text-[#7c3aed] hover:text-[#6d28d9] inline-flex items-center gap-1">
                      Visit AI NOVA Marketplace
                      <span>↗</span>
                    </button>
                  </div>
                </SlideInOnScroll>
              </div>

              {/* Container Registry */}
              <div className="py-8 grid gap-6 md:grid-cols-[0.2fr_0.95fr_1.1fr] items-start">
                <div className="flex justify-start">
                  <div className="w-16 h-16 rounded-full border-2 border-[#c4b5fd] flex items-center justify-center">
                    <Server className="w-7 h-7 text-[#8b5cf6]" />
                  </div>
                </div>
                <div>
                  <SlideInOnScroll from="left">
                    <h3 className="text-lg font-semibold text-[#3b2c70] mb-2">
                      Find the containerized services you need
                      <br className="hidden md:block" /> in AI NOVA Container
                      Registry
                    </h3>
                  </SlideInOnScroll>
                </div>
                <SlideInOnScroll from="right">
                  <div className="space-y-2 text-sm">
                    <p className="text-[#4b3a82] leading-relaxed">
                      Browse GPU-ready Kubernetes images to accelerate
                      application development, deployment, and optimization.
                    </p>
                    <button className="text-sm font-semibold text-[#7c3aed] hover:text-[#6d28d9] inline-flex items-center gap-1">
                      Read the datasheet
                      <span>↗</span>
                    </button>
                  </div>
                </SlideInOnScroll>
              </div>

              {/* Partner ecosystem */}
              <div className="py-8 grid gap-6 md:grid-cols-[0.2fr_0.95fr_1.1fr] items-start">
                <div className="flex justify-start">
                  <div className="w-16 h-16 rounded-full border-2 border-[#c4b5fd] flex items-center justify-center">
                    <Globe2 className="w-7 h-7 text-[#8b5cf6]" />
                  </div>
                </div>
                <div>
                  <SlideInOnScroll from="left">
                    <h3 className="text-lg font-semibold text-[#3b2c70] mb-2">
                      Draw on services from our partners
                      <br className="hidden md:block" />
                      to deploy and optimize your cloud applications
                    </h3>
                  </SlideInOnScroll>
                </div>
                <SlideInOnScroll from="right">
                  <div className="space-y-2 text-sm">
                    <p className="text-[#4b3a82] leading-relaxed">
                      Work with leading IaaS, PaaS, and SaaS providers to build
                      enterprise-grade cloud solutions—without lock-in.
                    </p>
                    <button className="text-sm font-semibold text-[#7c3aed] hover:text-[#6d28d9] inline-flex items-center gap-1">
                      Learn more about AI NOVA Cloud Alliance
                      <span>↗</span>
                    </button>
                  </div>
                </SlideInOnScroll>
              </div>
            </div>
          </div>

          {/* Built for business */}
          <div>
            <div className="rounded-3xl bg-white/85 text-[#2a0f4c] px-8 py-10 border border-purple-200 shadow-[0_20px_60px_rgba(148,101,255,0.2)]">
              <h3 className="text-2xl font-bold mb-8">Built for business</h3>
              <div className="grid gap-6 md:grid-cols-4">
                {[
                  {
                    title: "API & Terraform",
                    desc: "Automate provisioning with a full-featured API and infrastructure-as-code tooling.",
                  },
                  {
                    title: "Global content delivery",
                    desc: "Accelerate apps with integrated networking, routing, and edge connectivity.",
                  },
                  {
                    title: "DDoS protection",
                    desc: "Protect workloads with multi-layer DDoS mitigation, firewalls, and private networking.",
                  },
                  {
                    title: "Bring your own IP",
                    desc: "Move existing IP ranges into our network and keep addressing consistent across regions.",
                  },
                ].map((card, index) => (
                  <FadeInCard key={card.title} delay={index * 0.15}>
                    <div className="rounded-2xl border border-purple-100 bg-purple-50/40 p-5 text-sm hover:border-[#a855f7]/60 hover:shadow-[0_10px_30px_rgba(148,101,255,0.28)] transition">
                      <p className="font-semibold mb-2 text-[#3b2c70]">
                        {card.title}
                      </p>
                      <p className="text-[13px] leading-relaxed text-[#554089]">
                        {card.desc}
                      </p>
                    </div>
                  </FadeInCard>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ADDITIONAL RESOURCES（紫白 + 可点击 FAQ） */}
      <section className="relative py-20 bg-gradient-to-b from-purple-50 via-white to-purple-100 text-[#2a0f4c]">
        {/* 背景柔光 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-[30rem] h-[30rem] bg-purple-300/25 rounded-full blur-3xl -left-24 -top-10" />
          <div className="absolute w-[34rem] h-[34rem] bg-purple-200/30 rounded-full blur-3xl right-[-10rem] bottom-[-8rem]" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 space-y-16">
          {/* 标题 */}
          <div className="text-center">
            <FadeInOnScroll>
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#3d1a6e]">
                Additional resources
              </h2>
              <p className="text-sm text-[#7b5bbf] mt-3">
                Review our FAQ and Cloud GPU Docs for more information.
              </p>
            </FadeInOnScroll>
          </div>

          {/* 上半：FAQ + Docs/API */}
          <div className="grid gap-8 md:grid-cols-[1.8fr_1.1fr] items-start">
            <SlideInOnScroll from="right">
              {/* Docs & API 侧边卡 */}
              <div className="space-y-6">
                {/* Documentation */}
                <div className="rounded-[24px] bg-white/85 backdrop-blur-xl border border-purple-200 px-6 py-6 shadow-[0_16px_50px_rgba(136,84,255,0.18)]">
                  <div className="flex items-center gap-2 text-[#8b5cf6] mb-2">
                    <FileCode2 className="w-4 h-4" />
                    <span className="text-xs font-semibold tracking-[0.18em] uppercase">
                      Documentation
                    </span>
                  </div>
                  <p className="text-[13px] text-[#4c3670] leading-relaxed">
                    Learn how to provision instances, manage storage, and
                    integrate with your AI workflows using step-by-step guides
                    and examples.
                  </p>
                  <button className="mt-3 text-xs font-semibold text-[#8b5cf6] underline underline-offset-4">
                    View docs →
                  </button>
                </div>
              </div>
            </SlideInOnScroll>
            {/* <SlideInOnScroll from="left">
              <div className="rounded-[28px] bg-white/85 backdrop-blur-xl border border-purple-200 shadow-[0_24px_80px_rgba(136,84,255,0.25)] overflow-hidden">
                <div className="grid md:grid-cols-[0.9fr_1.2fr]">
                  <div className="bg-gradient-to-br from-purple-50 via-purple-100/80 to-purple-200/50 border-r border-purple-200 px-6 py-6 flex flex-col">
                    <div className="flex items-center gap-2 text-[#8b5cf6] mb-4">
                      <HelpCircle className="w-4 h-4" />
                      <span className="text-xs font-semibold uppercase tracking-[0.18em]">
                        FAQ
                      </span>
                    </div>

                    <div className="space-y-2 text-sm max-h-52 md:max-h-60 overflow-y-auto pr-1 no-scrollbar">
                      {faqs.map((item, index) => {
                        const isActive = index === activeFaqIndex;
                        return (
                          <button
                            key={item.question}
                            onClick={() => setActiveFaqIndex(index)}
                            className={`w-full text-left rounded-xl px-3 py-2 transition ${
                              isActive
                                ? "bg-white shadow-[0_6px_18px_rgba(136,84,255,0.25)] text-[#3b186e] font-semibold"
                                : "text-[#6b4cae] hover:bg-purple-100/80 hover:text-[#4a2b86]"
                            }`}
                          >
                            {item.question}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="px-7 py-7 bg-white/90 flex flex-col justify-between">
                    <div>
                      <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#8b5cf6] mb-2">
                        Featured question
                      </p>
                      <h3 className="text-lg md:text-xl font-bold text-[#3d1a6e] mb-3">
                        {activeFaq.question}
                      </h3>
                      <p className="text-sm leading-relaxed text-[#4c3670]">
                        {activeFaq.answer}
                      </p>
                    </div>

                    <button className="mt-5 text-xs font-semibold text-[#8b5cf6] underline underline-offset-4">
                      Read all FAQ →
                    </button>
                  </div>
                </div>
              </div>
            </SlideInOnScroll> */}
            <SlideInOnScroll from="right">
              {/* Docs & API 侧边卡 */}
              <div className="space-y-6">
                {/* API */}
                <div className="rounded-[24px] bg-white/85 backdrop-blur-xl border border-purple-200 px-6 py-6 shadow-[0_16px_50px_rgba(136,84,255,0.18)]">
                  <div className="flex items-center gap-2 text-[#8b5cf6] mb-2">
                    <FileCode2 className="w-4 h-4" />
                    <span className="text-xs font-semibold tracking-[0.18em] uppercase">
                      API
                    </span>
                  </div>
                  <p className="text-[13px] text-[#4c3670] leading-relaxed">
                    Automate Cloud GPU provisioning with REST API, CLI tools,
                    and language SDKs. Integrate with your existing CI/CD and
                    MLOps stack.
                  </p>
                  <button className="mt-3 text-xs font-semibold text-[#8b5cf6] underline underline-offset-4">
                    Explore API →
                  </button>
                </div>
              </div>
            </SlideInOnScroll>
          </div>

          {/* Case Study + Pricing */}
          <div className="grid gap-8 md:grid-cols-[1.1fr_1.4fr]">
            <SlideInOnScroll from="left">
              {/* Case Study */}
              <div className="rounded-[26px] bg-white/85 backdrop-blur-xl border border-purple-200 px-7 py-7 shadow-[0_20px_60px_rgba(136,84,255,0.22)]">
                <div className="flex items-center gap-2 text-[#8b5cf6] mb-3">
                  <BookOpen className="w-4 h-4" />
                  <span className="text-xs font-semibold tracking-[0.18em] uppercase">
                    Case study
                  </span>
                </div>
                <p className="text-sm text-[#3c2966] leading-relaxed mb-4">
                  “Important aspects of a partner: consistent availability of
                  GPUs, as well as technical understanding of our configuration
                  requirements and maintenance needs for the AI workloads of our
                  business.”
                </p>
                <p className="text-xs text-[#7b5bbf] mb-1">
                  Dwight Churchill, COO at Captions
                </p>
                <button className="mt-2 text-xs font-semibold text-[#8b5cf6] underline underline-offset-4">
                  Read the case study →
                </button>
              </div>
            </SlideInOnScroll>
            <SlideInOnScroll from="right">
              {/* Pricing */}
              <div className="rounded-[26px] bg-gradient-to-br from-[#f5e9ff] via-white to-[#ecebff] border border-purple-200 px-7 py-7 shadow-[0_24px_80px_rgba(136,84,255,0.35)] flex flex-col justify-between">
                <div>
                  <p className="text-[11px] tracking-[0.18em] uppercase text-[#9b6fff] mb-2">
                    High performance, low price
                  </p>
                  <h3 className="text-xl md:text-2xl font-extrabold text-[#3d1a6e] mb-3">
                    Start your next GPU project today
                  </h3>
                  <p className="text-sm text-[#4c3670] leading-relaxed max-w-md">
                    Transparent, usage-based pricing with no long-term
                    commitment. Scale up for experiments and scale down when
                    you’re done.
                  </p>
                </div>

                <Link
                  to="/contact"
                  className="rounded-full bg-[#8b5cf6] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#7c3aed] transition"
                >
                  View pricing
                </Link>
              </div>
            </SlideInOnScroll>
          </div>
        </div>
      </section>
    </main>
  );
}
